import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Badge,
  Row,
  Col,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import SettingsPageHeader from '../components/SettingsPageHeader';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { companyAPI, companyGroupAPI, companyLocationAPI, clearCache } from '../utils/api';
import TimezoneSelect from '../components/TimezoneSelect';
import type { Company, CreateCompanyRequest, UpdateCompanyRequest } from '../types/auth';
import type { CompanyGroup, CompanyLocation, InheritableSettings } from '../types/companyGroup';

const CompanyManagement: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'companyManagement' });
  const { currentCompany, setCurrentCompany, userRolePermissionsLoading } = useAuth();
  const {
    canCreateCompanies,
    canAccessCompanyManagement
  } = useRolePermissions();

  const PPE_OPTIONS = [
    { label: t('helmet'), value: 'helmet' },
    { label: t('hairnetGloves'), value: 'hairnet_gloves' },
    { label: t('industrialGloves'), value: 'industrial_gloves' },
    { label: t('industrialJacket'), value: 'industrial_jacket' },
  ];

  const [companies, setCompanies] = useState<Company[]>([]);
  const [groups, setGroups] = useState<CompanyGroup[]>([]);
  const [locations, setLocations] = useState<CompanyLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Form states
  const [formData, setFormData] = useState<CreateCompanyRequest>({
    company_id: '',
    name: '',
    status: 1,
    email: '',
    phone: '',
    address: '',
    description: '',
    timezone: 'UTC',
    company_location_id: null,
    shifts_enabled: false,
    shift_auto_detect: false,
    payroll_enabled: false,
  });
  const [overrides, setOverrides] = useState<Partial<InheritableSettings>>({});
  const [shiftInherit, setShiftInherit] = useState(false);
  const [payrollInherit, setPayrollInherit] = useState(false);

  // Companies list is global (not filtered by sidebar). Locations dropdown for "Assign to location"
  // is scoped to the sidebar-selected company (currentCompany) — chosen approach per task spec.
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [compRes, groupsRes] = await Promise.all([
        companyAPI.getCompanies(),
        companyGroupAPI.listGroups(),
      ]);
      setCompanies(compRes.data);
      setGroups(groupsRes.data);

      const locationResponses = await Promise.all(
        groupsRes.data.map((group) => companyLocationAPI.listLocations(group.id))
      );
      setLocations(locationResponses.flatMap((res) => res.data));
    } catch {
      setError(t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearCache('companies');
    fetchData();
  }, [fetchData]);


  const resetForm = () => {
    setFormData({
      company_id: '',
      name: '',
      status: 1,
      email: '',
      phone: '',
      address: '',
      description: '',
      timezone: 'UTC',
      company_location_id: null,
      shifts_enabled: false,
      shift_auto_detect: false,
      payroll_enabled: false,
    });
    setOverrides({});
    setShiftInherit(false);
    setPayrollInherit(false);
    setSelectedCompany(null);
    setModalError(null);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      setModalError(null);

      const payload: Record<string, any> = {
        company_id: formData.company_id,
        name: formData.name,
        status: formData.status,
        email: formData.email || '',
        phone: formData.phone || '',
        company_location_id: formData.company_location_id ?? null,
        timezone: formData.timezone,
        ppe_types: overrides.ppe_types || [],
      };

      if (formData.company_location_id && shiftInherit) {
        payload.shifts_enabled = null;
        payload.shift_auto_detect = null;
      } else {
        payload.shifts_enabled = formData.shifts_enabled ?? false;
        payload.shift_auto_detect = formData.shift_auto_detect ?? false;
      }
      if (formData.company_location_id && payrollInherit) {
        payload.payroll_enabled = null;
      } else {
        payload.payroll_enabled = formData.payroll_enabled ?? false;
      }

      if (formData.description) payload.description = formData.description;
      if (formData.address) payload.address = formData.address;

      if (formData.company_location_id) {
        const keys: (keyof InheritableSettings)[] = [
          'timezone', 'address', 'repo_th', 'valid_th_from', 'valid_th_to', 'similarity_addition', 'ppe_types', 'ppe_min_confidence'
        ];
        keys.forEach(key => {
          let value = overrides[key];
          if (value !== undefined && value !== null) {
            payload[key] = value;
          }
        });
      } else {
        if (overrides.repo_th != null) payload.repo_th = overrides.repo_th;
        if (overrides.valid_th_from != null) payload.valid_th_from = overrides.valid_th_from;
        if (overrides.valid_th_to != null) payload.valid_th_to = overrides.valid_th_to;
        if (overrides.similarity_addition != null) payload.similarity_addition = overrides.similarity_addition;
        if (overrides.ppe_types != null) payload.ppe_types = overrides.ppe_types;
        if (overrides.ppe_min_confidence != null) payload.ppe_min_confidence = overrides.ppe_min_confidence;
      }

      console.log('📤 CompanyManagement: Create payload:', JSON.stringify(payload, null, 2));
      await companyAPI.createCompany(payload as CreateCompanyRequest);
      setSuccess(t('createSuccess'));
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (err: any) {
      setModalError(err.response?.data?.message || t('saveFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const sanitizePlaceholder = (value: any) => {
    if (typeof value === 'string' && (value === '[value-12]' || value.includes('[value-'))) {
      return '';
    }
    return value;
  };

  const handleEditClick = async (company: Company) => {
    setSelectedCompany(company);
    setFormData({
      company_id: company.company_id,
      name: company.name,
      status: company.status,
      email: company.email || '',
      phone: company.phone || '',
      address: sanitizePlaceholder(company.address),
      description: company.description || '',
      timezone: company.timezone || 'UTC',
      company_location_id: company.company_location_id,
      shifts_enabled: company.shifts_enabled ?? false,
      shift_auto_detect: company.shift_auto_detect ?? false,
      payroll_enabled: company.payroll_enabled ?? false,
    });
    setShiftInherit(company.company_location_id != null && company.shifts_enabled == null);
    setPayrollInherit(company.company_location_id != null && company.payroll_enabled == null);

    const initialOverrides: Partial<InheritableSettings> = {};
    const keys: (keyof InheritableSettings)[] = [
      'timezone', 'address', 'repo_th', 'valid_th_from', 'valid_th_to', 'similarity_addition', 'ppe_types', 'ppe_min_confidence'
    ];
    keys.forEach(key => {
      let value = (company as any)[key];
      
      // Sanitize placeholder strings
      if (typeof value === 'string' && (value === '[value-12]' || value.includes('[value-'))) {
        value = null;
      }

      if (value !== null && value !== undefined) {
        (initialOverrides as any)[key] = value;
      }
    });
    setOverrides(initialOverrides);
    setModalError(null);
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany) return;
    try {
      setActionLoading(true);
      setModalError(null);
      
      const updateData: UpdateCompanyRequest = {
        name: formData.name,
        status: formData.status,
        email: formData.email ?? undefined,
        phone: formData.phone ?? undefined,
        address: formData.address ?? undefined,
        description: formData.description ?? undefined,
        timezone: formData.timezone,
        company_location_id: formData.company_location_id,
      };

      if (formData.company_location_id && shiftInherit) {
        updateData.shifts_enabled = null;
        updateData.shift_auto_detect = null;
      } else {
        updateData.shifts_enabled = formData.shifts_enabled ?? false;
        updateData.shift_auto_detect = formData.shift_auto_detect ?? false;
      }
      if (formData.company_location_id && payrollInherit) {
        updateData.payroll_enabled = null;
      } else {
        updateData.payroll_enabled = formData.payroll_enabled ?? false;
      }

      const keys: (keyof InheritableSettings)[] = [
        'timezone', 'address', 'repo_th', 'valid_th_from', 'valid_th_to', 'similarity_addition', 'ppe_types', 'ppe_min_confidence'
      ];
      keys.forEach(key => {
        let value = overrides[key];
        
        // If assigned to a location, overrides take precedence (null means inherit)
        // If standalone, address and timezone from formData take precedence
        if ((key === 'address' || key === 'timezone') && !formData.company_location_id) {
          // Already set from formData in updateData initialization, do not overwrite
        } else {
          (updateData as any)[key] = value === undefined ? null : value;
        }
      });

      await companyAPI.updateCompany(selectedCompany.id, updateData);
      const refreshedCompanies = await companyAPI.getCompanies();
      setCompanies(refreshedCompanies.data);
      if (currentCompany?.id === selectedCompany.id) {
        const refreshedCurrentCompany = refreshedCompanies.data.find((company) => company.id === selectedCompany.id);
        if (refreshedCurrentCompany) {
          setCurrentCompany(refreshedCurrentCompany);
        }
      }
      setSuccess(t('updateSuccess'));
      setShowEditModal(false);
      resetForm();
    } catch (err: any) {
      setModalError(err.response?.data?.message || t('saveFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const renderCompanyFormFields = (isEdit: boolean = false) => {
    const isOrgCompany = formData.company_location_id != null;
    const selectedLocation = isOrgCompany
      ? locations.find((location) => location.id === formData.company_location_id)
      : undefined;
    const selectedGroup = selectedLocation
      ? groups.find((group) => group.id === selectedLocation.company_group_id)
      : undefined;
    const inheritedShiftsEnabled = selectedLocation?.shifts_enabled ?? selectedGroup?.shifts_enabled ?? false;
    const inheritedShiftAutoDetect = inheritedShiftsEnabled
      ? selectedLocation?.shift_auto_detect ?? selectedGroup?.shift_auto_detect ?? false
      : false;
    const effectiveShiftsEnabled = isOrgCompany && isEdit && selectedCompany?.effective_shifts_enabled != null
      ? selectedCompany.effective_shifts_enabled
      : inheritedShiftsEnabled;
    const effectiveShiftAutoDetect = isOrgCompany && isEdit && selectedCompany?.effective_shift_auto_detect != null
      ? selectedCompany.effective_shift_auto_detect
      : inheritedShiftAutoDetect;

    return (
    <Row className="g-3">
      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label className="fw-bold">{t('companyIdLabel')} <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            value={formData.company_id}
            onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
            required
            disabled={isEdit}
          />
        </Form.Group>
      </Col>
      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label className="fw-bold">{t('companyNameLabel')} <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </Form.Group>
      </Col>
      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label className="fw-bold">{t('emailAddress')}</Form.Label>
          <Form.Control
            type="email"
            value={formData.email || ''}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </Form.Group>
      </Col>
      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label className="fw-bold">{t('phoneNumber')}</Form.Label>
          <Form.Control
            type="tel"
            value={formData.phone || ''}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        </Form.Group>
      </Col>
      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label className="fw-bold">{t('status')}</Form.Label>
          <Form.Select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: Number(e.target.value) })}
          >
            <option value={1}>{t('active', { ns: 'common', defaultValue: 'Active' })}</option>
            <option value={2}>{t('inactive', { ns: 'common', defaultValue: 'Inactive' })}</option>
          </Form.Select>
        </Form.Group>
      </Col>

      
        
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">{t('timezone')} <span className="text-danger">*</span></Form.Label>
              <TimezoneSelect
                value={formData.timezone}
                onChange={(val) => setFormData({ ...formData, timezone: val || 'UTC' })}
                useDetectedAsDefault={!isEdit}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">{t('repositoryThreshold')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={overrides.repo_th ?? ''}
                onChange={(e) => setOverrides(prev => ({ ...prev, repo_th: e.target.value === '' ? null : parseFloat(e.target.value) }))}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">{t('validationThresholdFrom')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={overrides.valid_th_from ?? ''}
                onChange={(e) => setOverrides(prev => ({ ...prev, valid_th_from: e.target.value === '' ? null : parseFloat(e.target.value) }))}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">{t('validationThresholdTo')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={overrides.valid_th_to ?? ''}
                onChange={(e) => setOverrides(prev => ({ ...prev, valid_th_to: e.target.value === '' ? null : parseFloat(e.target.value) }))}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">{t('similarityAddition')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={overrides.similarity_addition ?? ''}
                onChange={(e) => setOverrides(prev => ({ ...prev, similarity_addition: e.target.value === '' ? null : parseFloat(e.target.value) }))}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">{t('ppeMinConfidence')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                max="1"
                placeholder="0.80"
                value={overrides.ppe_min_confidence ?? ''}
                onChange={(e) => setOverrides(prev => ({ ...prev, ppe_min_confidence: e.target.value === '' ? null : parseFloat(e.target.value) }))}
              />
              <Form.Text className="text-muted">
                {t('ppeMinConfidenceHint')}
              </Form.Text>
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold d-block">{t('ppeDetectionTypes')}</Form.Label>
              <div className="d-flex flex-wrap gap-3 an-settings-checkbox-grid">
                {PPE_OPTIONS.map(option => (
                  <Form.Check
                    key={option.value}
                    type="checkbox"
                    id={`ppe-${option.value}`}
                    label={option.label}
                    checked={overrides.ppe_types?.includes(option.value) || false}
                    onChange={(e) => {
                      const currentTypes = overrides.ppe_types || [];
                      let nextTypes;
                      if (e.target.checked) {
                        nextTypes = [...currentTypes, option.value];
                      } else {
                        nextTypes = currentTypes.filter(tp => tp !== option.value);
                      }
                      setOverrides(prev => ({ ...prev, ppe_types: nextTypes.length > 0 ? nextTypes : [] }));
                    }}
                  />
                ))}
              </div>
              <Form.Text className="text-muted">
                {t('ppeHint')}
              </Form.Text>
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group className="mb-3">
              {isOrgCompany && (
                <>
                <div className="an-toggle-field mb-3">
                  <Form.Check
                    type="switch"
                    id="shifts-inherit-toggle"
                    label={t('inheritFromOrganization')}
                    checked={shiftInherit}
                    onChange={(e) => {
                      const inherit = e.target.checked;
                      setShiftInherit(inherit);
                      setFormData({
                        ...formData,
                        shifts_enabled: inherit ? null : effectiveShiftsEnabled,
                        shift_auto_detect: inherit ? null : effectiveShiftAutoDetect,
                      });
                    }}
                  />
                  <Form.Text className="text-muted d-block">
                    {t('inheritFromOrganizationHint')}
                  </Form.Text>
                </div>
                <div className="an-toggle-field mb-3">
                  <Form.Check
                    type="switch"
                    id="payroll-inherit-toggle"
                    label={`${t('payroll', { defaultValue: 'Payroll' })} - ${t('inheritFromOrganization')}`}
                    checked={payrollInherit}
                    onChange={(e) => {
                      const inherit = e.target.checked;
                      setPayrollInherit(inherit);
                      const effectivePayroll = isOrgCompany && isEdit && selectedCompany?.effective_payroll_enabled != null ? selectedCompany.effective_payroll_enabled : (selectedLocation?.payroll_enabled ?? selectedGroup?.payroll_enabled ?? false);
                      setFormData({
                        ...formData,
                        payroll_enabled: inherit ? null : effectivePayroll,
                      });
                    }}
                  />
                  <Form.Text className="text-muted d-block">
                    {t('payrollInheritHint')}
                  </Form.Text>
                </div>
                </>
              )}
              {isOrgCompany && shiftInherit ? (
                <div className="an-toggle-field">
                  <Badge bg={effectiveShiftsEnabled ? 'success' : 'secondary'} className="fw-normal">
                    {effectiveShiftsEnabled ? t('inheritedEnabled') : t('inheritedDisabled')}
                  </Badge>
                  {effectiveShiftsEnabled && effectiveShiftAutoDetect && (
                    <div className="text-muted small mt-1">
                      {t('shiftAutoDetect')}: {t('enabled', { ns: 'common', keyPrefix: 'status' })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-3 an-settings-checkbox-grid">
                  <div className="an-toggle-field">
                    <Form.Check
                      type="switch"
                      id="shifts-enabled-toggle"
                      label={t('shiftsEnabled')}
                      checked={formData.shifts_enabled ?? false}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setFormData({
                          ...formData,
                          shifts_enabled: enabled,
                          shift_auto_detect: enabled ? formData.shift_auto_detect : false,
                        });
                      }}
                    />
                    <Form.Text className="text-muted d-block">
                      {t('shiftsEnabledHint')}
                    </Form.Text>
                  </div>
                  {formData.shifts_enabled && <div className="an-toggle-field">
                    <Form.Check
                      type="switch"
                      id="shift-auto-detect-toggle"
                      label={t('shiftAutoDetect')}
                      checked={formData.shift_auto_detect ?? false}
                      onChange={(e) => setFormData({ ...formData, shift_auto_detect: e.target.checked })}
                    />
                    <Form.Text className="text-muted d-block">
                      {t('shiftAutoDetectHint')}
                    </Form.Text>
                  </div>}
                </div>
              )}
              {isOrgCompany && !payrollInherit && (
                <div className="an-toggle-field mt-3">
                  <Form.Check
                    type="switch"
                    id="payroll-enabled-toggle"
                    label={t('payroll')}
                    checked={formData.payroll_enabled ?? false}
                    onChange={(e) => setFormData({ ...formData, payroll_enabled: e.target.checked })}
                  />
                  <Form.Text className="text-muted d-block">
                    {t('payrollEnabledHint')}
                  </Form.Text>
                </div>
              )}
              {isOrgCompany && payrollInherit && (
                <div className="an-toggle-field mt-3">
                  <Badge bg={(selectedLocation?.payroll_enabled ?? selectedGroup?.payroll_enabled ?? false) ? 'success' : 'secondary'} className="fw-normal">
                    {(selectedLocation?.payroll_enabled ?? selectedGroup?.payroll_enabled ?? false) ? t('inheritedEnabled') : t('inheritedDisabled')}
                  </Badge>
                </div>
              )}
              {!isOrgCompany && (
                <div className="an-toggle-field mt-3">
                  <Form.Check
                    type="switch"
                    id="payroll-enabled-standalone"
                    label={t('payroll')}
                    checked={formData.payroll_enabled ?? false}
                    onChange={(e) => setFormData({ ...formData, payroll_enabled: e.target.checked })}
                  />
                  <Form.Text className="text-muted d-block">
                    {t('payrollEnabledHint')}
                  </Form.Text>
                </div>
              )}
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group>
              <Form.Label className="fw-bold">{t('address')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Form.Group>
          </Col>
     

      <Col xs={12}>
        <Form.Group>
          <Form.Label className="fw-bold">{t('description')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </Form.Group>
      </Col>
    </Row>
    );
  };

  const settingsPath = currentCompany ? `/company/${currentCompany.company_id}/settings` : '/settings';

  if (userRolePermissionsLoading || loading) {
    return (
      <Layout title={t('title')}>
        <Container className="text-center an-enterprise-page">
          <Spinner animation="border" variant="primary" />
        </Container>
      </Layout>
    );
  }

  if (!canAccessCompanyManagement) {
    return (
      <Layout title={t('title')}>
        <Container className="an-enterprise-page">
          <Alert variant="danger">
            {t('noAccess')}
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-data-page an-settings-page an-page-shell">
        <SettingsPageHeader
          title={t('title')}
          settingsPath={settingsPath}
        />

        <Alert variant="info" className="shadow-sm border-0 bg-white mb-4">
          <div className="d-flex align-items-center">
            <div className="me-3 fs-4 text-info">
              <i className="bi bi-info-circle-fill"></i>
            </div>
            <div>
              <h6 className="fw-bold mb-1">{t('infoTitle')}</h6>
              <p className="mb-0 small text-muted" dangerouslySetInnerHTML={{ __html: t('infoBody') }} />
            </div>
          </div>
        </Alert>

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card className="shadow-sm border-0 an-settings-table-card">
          <Card.Header className="py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h5 className="mb-0 fw-bold">{t('companiesList')}</h5>
            {canCreateCompanies && (
              <Button variant="primary" size="sm" onClick={() => { resetForm(); setShowCreateModal(true); }}>
                <i className="bi bi-plus-lg"></i> {t('addCompany')}
              </Button>
            )}
          </Card.Header>
          <Card.Body className="p-0">
            <div className="d-md-none p-3">
              {companies.length === 0 ? (
                <div className="text-center py-5 text-muted">{t('noCompanies')}</div>
              ) : (
                companies.map((company) => (
                  <Card key={company.id} className="an-data-card border-0 rounded-4 mb-3">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                        <div>
                          <div className="fw-bold">{company.name}</div>
                          <div className="small text-muted">ID: {company.company_id}</div>
                        </div>
                        <Badge bg={company.status === 1 ? 'success' : 'danger'}>
                          {company.status === 1 ? t('active', { ns: 'common', defaultValue: 'Active' }) : t('inactive', { ns: 'common', defaultValue: 'Inactive' })}
                        </Badge>
                      </div>

                      <div className="small text-muted mb-3">{company.email}</div>

                      <div className="d-flex justify-content-end">
                        <Button variant="link" className="p-1 text-primary" onClick={() => handleEditClick(company)}>
                          <i className="bi bi-pencil"></i> <span className="ms-1">{t('edit', { ns: 'common', keyPrefix: 'buttons' })}</span>
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ))
              )}
            </div>

            <div className="d-none d-md-block">
              <Table responsive hover className="mb-0 align-middle an-settings-table">
                <thead className="bg-light">
                  <tr>
                    <th className="px-4">{t('name')}</th>
                    <th>{t('id')}</th>
                    <th>{t('email')}</th>
                    <th>{t('status')}</th>
                    <th className="text-end px-4">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5 text-muted">
                        {t('noCompanies')}
                      </td>
                    </tr>
                  ) : (
                    companies.map((company) => (
                      <tr key={company.id}>
                        <td className="px-4 fw-medium">{company.name}</td>
                        <td><code className="small">{company.company_id}</code></td>
                        <td className="text-muted small">{company.email}</td>
                        <td>
                          <Badge bg={company.status === 1 ? 'success' : 'danger'}>
                            {company.status === 1 ? t('active', { ns: 'common', defaultValue: 'Active' }) : t('inactive', { ns: 'common', defaultValue: 'Inactive' })}
                          </Badge>
                        </td>
                        <td className="text-end px-4">
                          <Button variant="link" className="p-1 text-primary" onClick={() => handleEditClick(company)}>
                            <i className="bi bi-pencil"></i> <span className="ms-1">{t('edit', { ns: 'common', keyPrefix: 'buttons' })}</span>
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* Create Modal */}
        <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
          <Form onSubmit={handleCreate}>
            <Modal.Header closeButton>
              <Modal.Title>{t('addNewCompany')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {modalError && <Alert variant="danger">{modalError}</Alert>}
              {renderCompanyFormFields(false)}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>{t('cancel', { ns: 'common', keyPrefix: 'buttons' })}</Button>
              <Button variant="primary" type="submit" disabled={actionLoading}>
                {actionLoading ? <Spinner animation="border" size="sm" /> : t('createCompany')}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Edit Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
          <Form onSubmit={handleUpdate}>
            <Modal.Header closeButton>
              <Modal.Title>{t('editCompany')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {modalError && <Alert variant="danger">{modalError}</Alert>}
              {renderCompanyFormFields(true)}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowEditModal(false)}>{t('cancel', { ns: 'common', keyPrefix: 'buttons' })}</Button>
              <Button variant="primary" type="submit" disabled={actionLoading}>
                {actionLoading ? <Spinner animation="border" size="sm" /> : t('saveChanges')}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </Layout>
  );
};

export default CompanyManagement;
